import React, { useEffect, useState } from "react";
import { setPageMeta } from "../seo/setPageMeta";

export default function PrayerRequest() {
  const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    request: "",
    anonymous: false,
    website: "",
  });

  const [requests, setRequests] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState(false);

  const [status, setStatus] = useState({
    loading: false,
    message: "",
    type: "",
  });

  const [showForm, setShowForm] = useState(false);
  const [prayedLoadingId, setPrayedLoadingId] = useState("");
  const [prayedIds, setPrayedIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem("cr_prayed_ids") || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    setPageMeta({
      title: "Prayer Requests | Celebrate Recovery Independence KY",
      description:
        "Submit a prayer request to Celebrate Recovery in Independence, Kentucky, and view approved public prayer requests from the community.",
      canonical: "https://www.crindependenceky.com/prayer-request",
    });

    fetchPrayerRequests();
  }, []);

  async function fetchPrayerRequests() {
    try {
      const response = await fetch(`${API_URL}/api/prayer-requests`);
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json")) {
        const text = await response.text();
        throw new Error(`Expected JSON but got: ${text.slice(0, 120)}`);
      }

      const data = await response.json();
      setRequests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Failed to fetch prayer requests:", error);
      setListError(true);
    } finally {
      setListLoading(false);
    }
  }

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setStatus({ loading: true, message: "", type: "" });

    try {
      const response = await fetch(`${API_URL}/api/prayer-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          request: formData.request,
          anonymous: formData.anonymous,
          website: formData.website,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to save prayer request");
      }

      setStatus({
        loading: false,
        message:
          "Your prayer request has been submitted and is awaiting review before appearing publicly.",
        type: "success",
      });

      setFormData({ name: "", email: "", request: "", anonymous: false, website: "" });
    } catch (error) {
      setStatus({
        loading: false,
        message: error.message || "There was a problem sending your prayer request.",
        type: "error",
      });
    }
  }

  async function handlePrayed(id) {
    if (prayedIds.has(id)) return;
    setPrayedLoadingId(id);

    try {
      const response = await fetch(`${API_URL}/api/prayer-requests/${id}/prayed`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update prayer count");
      }

      setRequests((prev) =>
        prev.map((item) =>
          item._id === id
            ? {
                ...item,
                prayerCount:
                  typeof data.prayerCount === "number"
                    ? data.prayerCount
                    : (item.prayerCount || 0) + 1,
              }
            : item
        )
      );

      const updated = new Set(prayedIds);
      updated.add(id);
      setPrayedIds(updated);
      localStorage.setItem("cr_prayed_ids", JSON.stringify([...updated]));
    } catch (error) {
      console.error("Failed to increment prayer count:", error);
    } finally {
      setPrayedLoadingId("");
    }
  }

  return (
    <section>
      <h2 className="page-title">Prayer Requests</h2>
      <p className="page-intro">
        Submit a prayer request below. Public requests are reviewed before they
        appear on the site.
      </p>

      <div className="grid-2">
        <div className="form-card">
          <button
            type="button"
            className="btn btn-primary prayer-form-toggle"
            onClick={() => setShowForm((prev) => !prev)}
          >
            {showForm ? "Cancel" : "Submit a Prayer Request"}
          </button>

          <div className={`prayer-form-body${showForm ? " open" : ""}`}>
          <h3>Submit a Prayer Request</h3>

          <form className="form-grid" onSubmit={handleSubmit}>
            <div className="form-grid two-col">
              <div>
                <label className="label" htmlFor="pr-name">Name</label>
                <input
                  className="input"
                  id="pr-name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  disabled={formData.anonymous}
                />
              </div>

              <div>
                <label className="label" htmlFor="pr-email">Email</label>
                <input
                  className="input"
                  id="pr-email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={formData.anonymous}
                />
              </div>
            </div>

            <div>
              <label className="label" htmlFor="pr-request">Prayer Request</label>
              <textarea
                className="textarea"
                id="pr-request"
                name="request"
                value={formData.request}
                onChange={handleChange}
                required
              />
            </div>

            <div
              style={{
                position: "absolute",
                left: "-9999px",
                width: "1px",
                height: "1px",
                overflow: "hidden",
              }}
              aria-hidden="true"
            >
              <label htmlFor="website">Website</label>
              <input
                id="website"
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex="-1"
              />
            </div>

            <label>
              <input
                type="checkbox"
                name="anonymous"
                checked={formData.anonymous}
                onChange={handleChange}
              />{" "}
              Submit this prayer request anonymously
            </label>

            <p className="help-text">
              If anonymous is selected, your name and email will not appear with
              the request.
            </p>

            <button
              className="btn btn-primary"
              type="submit"
              disabled={status.loading}
            >
              {status.loading ? "Sending..." : "Submit Prayer Request"}
            </button>
          </form>

          {status.message && (
            <p
              className={`status-message ${
                status.type === "success" ? "status-success" : "status-error"
              }`}
            >
              {status.message}
            </p>
          )}
          </div>
        </div>

        <div className="card prayer-list-card">
          <h3>Current Prayer Requests</h3>

          {listLoading ? (
            <p>Loading prayer requests...</p>
          ) : listError ? (
            <p className="status-message status-error">
              Could not load prayer requests. Please refresh the page.
            </p>
          ) : requests.length === 0 ? (
            <p>No approved prayer requests have been posted yet.</p>
          ) : (
            <div className="video-list">
              {requests.map((item) => (
                <div key={item._id} className="video-item">
                  <h4>{item.name}</h4>
                  <p className="video-meta">
                    {item.createdAt ? new Date(item.createdAt).toLocaleString() : ""}
                  </p>
                  <p>{item.request}</p>

                  <div className="prayer-actions">
                    <button
                      type="button"
                      className={`btn prayed-btn ${
                        prayedIds.has(item._id) ? "btn-secondary" : "btn-primary"
                      }`}
                      onClick={() => handlePrayed(item._id)}
                      disabled={prayedLoadingId === item._id || prayedIds.has(item._id)}
                    >
                      {prayedIds.has(item._id)
                        ? "Prayed"
                        : prayedLoadingId === item._id
                        ? "Praying..."
                        : "Pray for this request"}
                    </button>

                    <span className="prayer-count">
                      {item.prayerCount || 0} prayer
                      {(item.prayerCount || 0) === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
